import { useState, useEffect } from "react";
import "./common.scss";

const UploadFile = ({ data, setAnswer, formValidation = true, formClass }) => {
  const [selectedOption, setSelectedOption] = useState(
    data.answer?.value || ""
  );
  const [validForm, setFormValidation] = useState(formValidation);
  const [isUploading, setIsUploading] = useState(false);

  // Function to upload file to S3 using presigned URL
  async function uploadFileToS3(file, uploadUrl) {
    try {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`
        );
      }

      return response;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  }

  // Function to format accepted file types for display
  const formatAcceptedTypes = (acceptedTypes) => {
    if (!acceptedTypes || acceptedTypes === "*") {
      return "All file types";
    }

    // Split by comma and clean up the types
    const types = acceptedTypes.split(",").map((type) => type.trim());

    // Convert MIME types and extensions to readable format
    const readableTypes = types.map((type) => {
      if (type.startsWith(".")) {
        return type.toUpperCase();
      } else if (type.includes("/")) {
        // Convert MIME types to readable format
        const [category, subtype] = type.split("/");
        if (subtype === "*") {
          return category.toUpperCase();
        }
        return subtype.toUpperCase();
      }
      return type.toUpperCase();
    });

    return readableTypes.join(", ");
  };

  // Function to remove selected file
  const removeFile = () => {
    setSelectedOption("");
    setAnswer(data, { value: null });
    // Reset the file input
    const fileInput = document.getElementById(data?.key);
    if (fileInput) {
      fileInput.value = "";
    }
    // Update form validation if required
    if (data?.validate?.required) {
      setFormValidation(false);
    }
  };

  useEffect(() => {
    setFormValidation(formValidation);
    if (data?.answer) {
      setSelectedOption(data.answer?.value);
    } else setSelectedOption("");
  }, [data, formValidation]);

  return (
    <div className={`w-full ${formClass}`}>
      <div className="form-title pt-3">
        {data?.label}
        {data?.validate?.required ? (
          <span className={!formValidation ? "text-red-700" : "text-gray-600"}>
            {" "}
            *
          </span>
        ) : null}
      </div>
      <form className="flex-grow" onSubmit={(e) => e.preventDefault()}>
        {/* Prevent form submission */}
        <div className="radio-common">
          <label className="relative">
            <input
              type="file"
              id={data?.key}
              accept={data?.acceptedFileType || "*"}
              disabled={data?.disabled || false}
              style={{ display: "none" }} // Hide the default file input
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;

                setIsUploading(true);
                uploadFileToS3(file, data?.uploadUrl)
                  .then((response) => {
                    if (response.ok) {
                      // If upload is successful, set the answer
                      setSelectedOption(file);
                      setAnswer(data, { value: {name: file?.name, fileId: data?.fileId} });
                      setFormValidation(true);
                    } else {
                      // Handle upload failure
                      console.error("File upload failed");
                    }
                  })
                  .catch((error) => {
                    console.error("Error uploading file:", error);
                  })
                  .finally(() => {
                    setIsUploading(false);
                  });
              }}
              className="text-field py-3 px-2 "
            />

            <div className="flex items-center gap-2">
              <button
                className={`rounded px-2 py-1 my-3 mx-2 cursor-pointer primary_background flex items-center gap-2 ${
                  isUploading || data?.disabled
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                type="button" // Prevent the button from submitting the form
                onClick={() => document.getElementById(`${data?.key}`).click()}
                disabled={data?.disabled || isUploading}
              >
                {isUploading ? (
                  <>
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Uploading...
                  </>
                ) : (
                  "Choose File"
                )}
              </button>
            </div>

            {isUploading ? (
              <div className="px-3 mb-2 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded p-3">
                <svg
                  className="animate-spin w-4 h-4 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-blue-600 font-medium">
                  Uploading file...
                </span>
              </div>
            ) : selectedOption?.name ? (
              <div className="px-3 mb-2 flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-3">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span className="text-gray-700 font-medium">
                    {selectedOption?.name}
                  </span>
                </div>
                <button
                  className={`text-gray-400 hover:text-red-500 transition-colors duration-200 p-1 ${
                    isUploading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  type="button"
                  onClick={removeFile}
                  disabled={isUploading}
                  title="Remove file"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : null}

            {/* Show accepted file types */}
              <div className="px-2 text-gray-500 text-sm mb-1">
                <b>Accepted file types: </b>
                {formatAcceptedTypes(data.acceptedFileType)}
              </div>

            {data?.description && (
              <div className="px-2 text-gray-500 text-sm">
                {data?.description}
              </div>
            )}
          </label>
        </div>
      </form>
      {data?.validate?.required && !formValidation ? (
        <span className="text-red-700 font-light text-xs">
          {data?.invalidMessage || "Required Field"}
        </span>
      ) : null}
    </div>
  );
};

export default UploadFile;
